function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import * as React from 'react';
import math from 'polished/lib/math/math';
import rgba from 'polished/lib/color/rgba';
import position from 'polished/lib/shorthands/position';
import size from 'polished/lib/shorthands/size';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import typography from '../utils/typography';
import { useTheme } from '@veneer/theme';
var useStyles = _ref => {
  var {
    appearance,
    colorScheme,
    expanded,
    focusVisible,
    hoverable,
    isIconButton,
    loading,
    negative: negativeProp,
    renderLeadingIcon,
    renderTrailingIcon,
    small
  } = _ref;
  var theme = useTheme();
  return React.useMemo(() => {
    var hasIcon = renderLeadingIcon || renderTrailingIcon;
    var isGhost = appearance === 'ghost';
    var isHc = theme.mode === 'contrast';
    var innerColor = color => colorScheme === 'warning' ? theme.palette.foreground.default : color;
    var buttonColor = color => theme.mode === 'dark' || theme.mode === 'contrast' ? color : innerColor(color);
    var isWarningDark = (theme.mode === 'dark' || theme.mode === 'contrast') && colorScheme === 'warning';
    var spacings = {
      regular: {
        borderRadius: theme.borderRadius,
        marginIcon: theme.layout.size2,
        paddingHorizontal: isGhost ? theme.orbitalFour : theme.orbitalSix,
        paddingHorizontalWithIcon: isGhost ? theme.orbitalThree : theme.orbitalFour,
        paddingIconButton: theme.orbitalIntermediate,
        paddingVertical: theme.orbitalIntermediate
      },
      small: {
        borderRadius: theme.borderRadius3,
        marginIcon: theme.layout.size2,
        marginTrailingIcon: theme.layout.size2,
        paddingHorizontal: isGhost ? theme.layout.size3 : theme.layout.size5,
        paddingHorizontalWithIcon: isGhost ? theme.layout.size2 : theme.layout.size3,
        paddingIconButton: '6px',
        paddingVertical: '6px'
      }
    };
    var spacing = small ? spacings.small : spacings.regular;
    var color = colorScheme === 'default' ? theme.palette.primary : theme.palette[colorScheme];

    // Map colors for the deprecated negative prop
    if (negativeProp && colorScheme === 'default') {
      var background = transparency => isHc ? theme.color.highContrastEigengrau : rgba(theme.color.gray0, transparency);
      color = {
        default: theme.color.white,
        hover: theme.color.gray0,
        active: theme.color.gray1,
        background: background(0.1),
        backgroundLight: background(0.05),
        foreground: theme.color.gray10
      };
    }
    var padding = isIconButton ? [spacing.paddingIconButton] : [spacing.paddingVertical, spacing.paddingHorizontal];
    var minWidth = theme.density === 'high' ? '104px' : '112px';
    return {
      // Style to normalize button appearance on browsers
      base: {
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 0,
        margin: 0,
        overflow: 'visible',
        textDecoration: 'none',
        textTransform: 'none',
        userSelect: 'none',
        verticalAlign: 'middle',
        WebkitTapHighlightColor: 'transparent',
        whiteSpace: 'nowrap',
        // Disable focus ring
        outline: 0,
        boxShadow: 'none',
        '&::-moz-focus-inner': {
          borderStyle: 'none'
        }
      },
      root: _extends({
        alignItems: 'center',
        borderRadius: spacing.borderRadius,
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'inline-flex',
        justifyContent: 'center'
      }, typography(theme).label, {
        minWidth,
        padding: padding.join(' '),
        position: 'relative',
        transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
        width: expanded ? '100%' : undefined
      }, highContrastMediaQuery(theme), !isIconButton && hasIcon && {
        [renderLeadingIcon ? 'paddingLeft' : 'paddingRight']: math("".concat(spacing.paddingHorizontalWithIcon, " - 1px"))
      }, {
        '&::after': _extends({
          border: "2px solid ".concat(theme.palette.primary.default),
          borderRadius: spacing.borderRadius,
          content: "' '",
          opacity: 0,
          transition: 'all 50ms'
        }, position('absolute', 0), focusVisible && _extends({
          borderRadius: "calc(".concat(spacing.borderRadius, " + 2px)"),
          opacity: 1
        }, position('-4px')))
      }, appearance === 'primary' && _extends({
        backgroundColor: color.default,
        color: isWarningDark ? theme.palette.foreground.inverse : innerColor(color.foreground)
      }, hoverable && {
        '&:hover': {
          backgroundColor: color.hover
        }
      }, {
        '&:active': {
          backgroundColor: color.active
        }
      }), appearance === 'secondary' && _extends({
        border: "1px solid ".concat(buttonColor(color.default)),
        color: buttonColor(color.default),
        padding: padding.map(v => math("".concat(v, " - 1px"))).join(' ')
      }, hoverable && {
        '&:hover': _extends({
          backgroundColor: color.backgroundLight
        }, !negativeProp && {
          borderColor: buttonColor(color.hover),
          color: buttonColor(color.hover)
        })
      }, {
        '&:active': _extends({
          backgroundColor: color.background
        }, !negativeProp && {
          borderColor: buttonColor(color.active),
          color: buttonColor(color.active)
        })
      }), appearance === 'ghost' && _extends({
        color: buttonColor(color.default),
        minWidth: '80px'
      }, hoverable && {
        '&:hover': {
          backgroundColor: color.backgroundLight,
          color: buttonColor(color.hover)
        }
      }, {
        '&:active': {
          backgroundColor: color.background,
          color: buttonColor(color.active)
        }
      }), isIconButton && {
        minWidth: 'auto'
      }),
      disabled: _extends({
        color: theme.palette.foreground.disabled,
        cursor: 'default',
        pointerEvents: 'none'
      }, appearance === 'primary' && {
        backgroundColor: theme.palette.background.disabled
      }, appearance === 'secondary' && {
        borderColor: theme.palette.foreground.disabled
      }, loading && {
        color: 'transparent'
      }),
      label: {
        display: 'inherit',
        margin: '2px 0'
      },
      spinner: {
        color: theme.palette.foreground.disabled,
        display: 'inline-block',
        left: 'calc(50% - 12px)',
        position: 'absolute',
        top: 'calc(50% - 12px)',
        zIndex: 1
      },
      icon: {
        display: 'inherit',
        '& > *:nth-of-type(1)': _extends({
          color: 'inherit'
        }, size(theme.layout.size6))
      },
      leadingIcon: {
        marginRight: spacing.marginIcon
      },
      trailingIcon: {
        marginLeft: spacing.marginIcon
      }
    };
  }, [appearance, colorScheme, expanded, focusVisible, hoverable, isIconButton, loading, negativeProp, renderLeadingIcon, renderTrailingIcon, small, theme]);
};
export default useStyles;